import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import api from "../../../API/axios";
import { useGroupsApi } from "../../../hooks/useGroupsApi";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Cancel from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";
import { countries } from "../../../utils/country";
import useNotification from "../../../hooks/useNotification";
import NotificationSnackbar from "../../../components/NotificationSnackbar";

import { handleFieldChange } from "../../../utils/handleFieldChange";
import { useClientsApi } from "../../../hooks/useClientsApi";
import clientsApi from "../../../API/clientsApi";
import {
  sanitizeInput,
  sanitizeNumberOnly,
  sanitizeNumericInput,
} from "../../../utils/inputSanitizer";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import paymentApi from "../../../API/paymentApi";

function PaymentTab({ setTab }) {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const [fields, setFields] = useState({
    apiKey: "",
    apiSecret: "",
    bookingUrl: "",
    fallback: false,
  });
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
 
  const { id: clientId } = useParams();
  // Use clients API helpers for acknowledgement

  // Track if API key/secret are stored
  const [hasApiKey, setHasApiKey] = useState(false);


 
  
  const [hasApiSecret, setHasApiSecret] = useState(false);
  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    paymentApi
      .getSettings(clientId)
      .then((data) => {
        setFields({
          apiKey: data.hasPaymentApiKey ? "" : "",
          apiSecret: data.hasPaymentApiSecret ? "" : "",
          bookingUrl: data.bookingFormUrl || "",
          fallback: !!data.usePlatformDefaultIfMissing,
        });
        setHasApiKey(!!data.hasPaymentApiKey);
        setHasApiSecret(!!data.hasPaymentApiSecret);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [clientId, success]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFields((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    const errs = {};
    
    // Only require API credentials if fallback is disabled
    if (!fields.fallback) {
      if (!fields.apiKey) errs.apiKey = "API Key is required when fallback is disabled";
      if (!fields.apiSecret) errs.apiSecret = "API Secret is required when fallback is disabled";
    }
    
    // Booking URL is always required
    if (!fields.bookingUrl) {
      errs.bookingUrl = "Booking Form URL is required";
    } else if (!fields.bookingUrl.startsWith("https://")) {
      errs.bookingUrl = "URL must start with https://";
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
 
  return (
    <>
      <Box
        sx={{
          p: 2,
          backgroundColor: "#f8f9fa",
          minHeight: 460,
          position: "relative",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
        >
          Payment Details
        </Typography>
        <Box sx={{ display: "flex", gap: 4 }}>
          {/* Left Column */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              flex: 1,
              maxWidth: 400,
            }}
          >
            <TextField
              label="Payment API Key"
              autoFocus 
              name="apiKey"
              type={showKey ? "text" : "password"}
              value={fields.apiKey}
              onChange={handleChange}
              error={!!errors.apiKey}
              helperText={hasApiKey ? "Stored (••••••••)" : errors.apiKey}
              placeholder={hasApiKey ? "••••••••" : "Enter Payment API Key"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowKey((v) => !v)}
                      edge="end"
                    >
                      {showKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              required={!fields.fallback}
              
              fullWidth
              sx={{ borderRadius: 2, background: "#fff" }}
            />
            <TextField
              label="Payment API Secret"
              name="apiSecret"
              type={showSecret ? "text" : "password"}
              value={fields.apiSecret}
              onChange={handleChange}
              error={!!errors.apiSecret}
              helperText={hasApiSecret ? "Stored (••••••••)" : errors.apiSecret}
              placeholder={
                hasApiSecret ? "••••••••" : "Enter Payment API Secret"
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowSecret((v) => !v)}
                      edge="end"
                    >
                      {showSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              required={!fields.fallback}
              fullWidth
              sx={{ borderRadius: 2, background: "#fff" }}
            />
            <TextField
              label="Booking Form URL"
              name="bookingUrl"
              value={fields.bookingUrl}
              onChange={handleChange}
              error={!!errors.bookingUrl}
              helperText={errors.bookingUrl}
              required
              fullWidth
              sx={{ borderRadius: 2, background: "#fff" }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={fields.fallback}
                  onChange={handleChange}
                  name="fallback"
                  color="primary"
                />
              }
              label="Enable Fallback Payment (optional)"
            />
          </Box>
        </Box>
        <Box sx={{ position: "absolute", bottom: 16, left: 16 }}>
          <Button
            variant="contained"
            onClick={() => setTab(3)}
            sx={{
              mt: 2,
              backgroundColor: "var(--color-primary)",
              "&:hover": { backgroundColor: "var(--color-secondary)" },
            }}
          >
            Previous
          </Button>
        </Box>
        <Box sx={{ position: "absolute", bottom: 16, right: 16 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (validate()) {
                setLoading(true);
                setSuccess(false);
                try {
                  // Only send values if not masked
                  const payload = {
                    paymentApiKey:
                      fields.apiKey === "********" ? undefined : fields.apiKey,
                    paymentApiSecret:
                      fields.apiSecret === "********"
                        ? undefined
                        : fields.apiSecret,
                    bookingFormUrl: fields.bookingUrl,
                    usePlatformDefaultIfMissing: fields.fallback,
                  };
                  const response = await paymentApi.updateSettings(
                    clientId,
                    payload
                  );
                  // Fix: paymentApi.updateSettings returns response.data directly
                  if (response.success) {
                    showNotification(response.message || "Payment settings saved successfully", "success");
                    setSuccess(true);
                    setTab(5); // Navigate to Custom tab
                  } else {
                    // Handle update failure
                    showNotification(response.message || "Failed to save payment settings", "error");
                  }
                } catch (e) {
                  console.error("Payment settings save error:", e);
                  showNotification("Failed to save payment settings", "error");
                }
                setLoading(false);
              }
            }}
          >
            Save & Next
          </Button>
        </Box>
      </Box>
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </>
  );
}

function TabPanel({ children, value, index }) {
  return (
    <div style={{ display: value === index ? "block" : "none", width: "100%" }}>
      <Box sx={{ p: 2 }}>{children}</Box>
    </div>
  );
}

function OperatorListModal({ open, onClose, operators }) {
  const isActiveFilterOperators = [
    {
      label: "Enabled",
      value: "enabled",
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return (params) => params.value === true;
      },
      InputComponent: null,
    },
    {
      label: "Disabled",
      value: "disabled",
      getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) return null;
        return (params) => params.value === false;
      },
      InputComponent: null,
    },
  ];
  const columns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
    {
      field: "ownerClientName",
      headerName: "Owner Client",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "isActive",
      headerName: "Op Status",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Enabled" : "Disabled"}
          color={params.value ? "success" : "error"}
          size="small"
        />
      ),
      filterOperators: isActiveFilterOperators,
    },
  ];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minWidth: 340,
          maxWidth: 500,
          p: 1,
          position: "relative",
        },
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "var(--color-primary)",
          zIndex: 1,
        }}
      >
        <Cancel fontSize="small" />
      </IconButton>
      <DialogTitle sx={{ pr: 4 }}>
        <Typography
          variant="h6"
          fontWeight="bold"
          textAlign="center"
          color="var(--color-primary)"
        >
          Operators Included in Groups
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, height: 340, maxHeight: 340 }}>
        <div style={{ height: 300, width: "100%", overflow: "auto" }}>
          <DataGrid
            rows={operators}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
            autoHeight={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EditClientTabs() {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const { fetchGroups, groups, fetchOperatorsByGroupId } = useGroupsApi();
  useEffect(() => {
    fetchGroups();
  }, []);

  const {
    client: clientData,
    loading: clientLoading,
    fetchClientById,
    createOrUpdateConsumerAcknowledgement,
      getConsumerAcknowledgement,
  } = useClientsApi();

  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [client, setClient] = useState(null);
   const [safeMessage, setSafeMessage] = useState("");
    const [fields, setFields] = useState([]);
        // textarea state
    const [savedMessage, setSavedMessage] = useState("");
  const [details, setDetails] = useState({
    name: "",
    email: "",
    telephone: "",
    address1: "",
    address2: "",
    county: "",
    city: "",
    postcode: "",
    country: "GB",
    enabled: true,
    secretKey: "",
    groups: [],
  });
  const [phoneCode, setPhoneCode] = useState(
    countries.find((c) => c.code === details.country)?.phoneCode || "+44"
  );
  // Security check for dangerous content
  const containsDangerousContent = (text) => {
    const dangerousPatterns = [
      /<script[\s\S]*?>[\s\S]*?<\/script>/gi,  // Script tags
      /<script[\s\S]*?>/gi,                      // Opening script tag
      /javascript\s*:/gi,                        // javascript: protocol
      /on\w+\s*=/gi,                             // Event handlers (onclick, onerror, etc.)
      /<iframe[\s\S]*?>/gi,                      // iframe tags
      /<object[\s\S]*?>/gi,                      // object tags
      /<embed[\s\S]*?>/gi,                       // embed tags
      /vbscript\s*:/gi,                          // vbscript: protocol
      /data\s*:\s*text\/html/gi,                 // data:text/html
    ];
    return dangerousPatterns.some(pattern => pattern.test(text));
  };

  const handleSubmitTab = async (e) => {
  if (e) e.preventDefault();
  
  // Check for whitespace-only input
  const trimmedMessage = safeMessage.trim();
  if (trimmedMessage.length === 0) {
    setErrors({ message: "Message cannot be empty or whitespace only" });
    return;
  }

  if (trimmedMessage.length < 10) {
    setErrors({ message: "Minimum 10 characters required" });
    return;
  }

  if (trimmedMessage.length > 160) {
    setErrors({ message: "Maximum 160 characters allowed" });
    return;
  }

  // Security: Block dangerous content like scripts
  if (containsDangerousContent(safeMessage)) {
    setErrors({ message: "Message contains prohibited content (scripts/code not allowed)" });
    showNotification("Scripts and code are not allowed in the message", "error");
    return;
  }

  if (!id) {
    showNotification("Client not selected", "error");
    return;
  }

  try {
    const payload = { message: safeMessage.trim() };
    // Use clientsApi directly to avoid triggering clientLoading state
    const res = await clientsApi.createOrUpdateConsumerAcknowledgement(id, payload);
    if (res && !res.error) {
      showNotification("Custom message saved", "success");
      setSavedMessage(safeMessage.trim());
      setErrors({});
    } else {
      const errorMsg = res?.error || res?.details || "Failed to save acknowledgement";
      showNotification(errorMsg, "error");
      setErrors({ message: errorMsg });
    }
  } catch (err) {
    console.error("Acknowledgement save error:", err);
    // Extract meaningful error message from response
    let errorMessage = "Failed to save acknowledgement";
    if (err?.response?.data) {
      const data = err.response.data;
      if (data.error) {
        errorMessage = data.error;
      } else if (data.details) {
        errorMessage = typeof data.details === 'string' ? data.details : data.details.join(', ');
      } else if (data.message) {
        errorMessage = data.message;
      } else if (typeof data === 'string') {
        errorMessage = data;
      }
    } else if (err?.message && !err.message.includes('status code')) {
      errorMessage = err.message;
    }
    showNotification(errorMessage, "error");
    setErrors({ message: errorMessage });
  }
};

  useEffect(() => {
      const fetchAck = async () => {
        try {
          // Use clientsApi directly to avoid triggering clientLoading state
          const res = await clientsApi.getConsumerAcknowledgement(id);
          // API returns { data: ack } per controller; support both shapes
          const ack = res?.data || res;
          if (ack && ack.message) {
            setSafeMessage(ack.message);
            setSavedMessage(ack.message);
          }
        } catch (err) {
          console.error("Failed to load acknowledgement:", err);
        }
      };

      if (id) fetchAck();
    }, [id]);
  
    const handleFieldChangeKeyValue = (id, key, value) => {
      setFields((prev) =>
        prev.map((f) => (f.id === id ? { ...f, key, value } : f))
      );
    };
  


  const handleFocus = (e) => {
    e.target.select(); // select all text on focus
  };

  useEffect(() => {
    const selected = countries.find((c) => c.code === details.country);
    setPhoneCode(selected ? selected.phoneCode : "+44");
  }, [details.country]);

  const [currency, setCurrency] = useState("GBP");
  const [creditBundles, setCreditBundles] = useState([
    { credits: "", amount: "", decimal: "", currency: "" },
  ]);
  const [fees, setFees] = useState({
    monthlyFee: "",
    monthlyFeeDecimal: "",
    perMessageReply: "",
    perMessageReplyDecimal: "",
    maxOperators: "",
    unlimited: true,
  });
  const [webDetails, setWebDetails] = useState({
    websiteUrl: "",
    smtpEnabled: false,
    smtpHost: "",
    smtpPort: "",
    smtpUsername: "",
    smtpPassword: "",
    smtpFrom: "",
    smtpSSL: false,
  });
  const [errors, setErrors] = useState({});
  const [operatorModalOpen, setOperatorModalOpen] = useState(false);
  const [operatorRows, setOperatorRows] = useState([]);

  useEffect(() => {
    fetchClientById(id);
  }, []);

  // Map clientData to state when it changes
  useEffect(() => {
    if (!clientData) return;

    // --- Phone code extraction logic ---
    let telephoneClient = clientData.contactNumber || "";
    let countryCode = clientData.Country || clientData.country || "GB";
    let phoneCode =
      countries.find((c) => c.code === countryCode)?.phoneCode || "+44";
    // Remove phone code from telephone if present
    if (telephoneClient.startsWith(phoneCode)) {
      telephoneClient = telephoneClient.replace(phoneCode, "").trim();
    }

    setClient(clientData);
    setDetails({
      name: clientData.name || "",
      email: clientData.email || "",
      telephone: telephoneClient,
      address1: clientData.address1 || "",
      address2: clientData.address2 || "",
      county: clientData.countyState || "",
      city: clientData.townCity || "",
      postcode: clientData.postCode || "",
      country: clientData.country || "GB",
      enabled: clientData.status === 1,
      secretKey: clientData.apiKey || "",
      groups: Array.isArray(clientData.groups)
        ? clientData.groups.map((g) => (typeof g === "object" ? g.id : g))
        : [],
    });
    setCurrency(
      clientData.creditBundles.length > 0
        ? clientData.creditBundles[0].currency || "GBP"
        : "GBP"
    );
    setCreditBundles(
      Array.isArray(clientData.creditBundles) &&
        clientData.creditBundles.length > 0
        ? clientData.creditBundles.map((b) => {
            let amountInt = "";
            let amountDec = "";
            if (
              b.amount !== undefined &&
              b.amount !== null &&
              b.amount !== ""
            ) {
              const [intPart, decPart] = String(b.amount).split(".");
              amountInt = intPart || "";
              amountDec = decPart || "";
            }
            return {
              credits: b.credits || "",
              amount: amountInt,
              decimal: amountDec,
            };
          })
        : [{ credits: "", amount: "", decimal: "" }]
    );
    let monthlyFeeInt = "";
    let monthlyFeeDec = "";
    if (
      clientData.fees?.monthlyFee !== undefined &&
      clientData.fees?.monthlyFee !== null &&
      clientData.fees?.monthlyFee !== ""
    ) {
      const [intPart, decPart] = String(clientData.fees.monthlyFee).split(".");
      monthlyFeeInt = intPart || "";
      monthlyFeeDec = decPart || "";
    }
    let perMessageReplyInt = "";
    let perMessageReplyDec = "";
    if (
      clientData.fees?.perMessageReply !== undefined &&
      clientData.fees?.perMessageReply !== null &&
      clientData.fees?.perMessageReply !== ""
    ) {
      const [intPart, decPart] = String(clientData.fees.perMessageReply).split(
        "."
      );
      perMessageReplyInt = intPart || "";
      perMessageReplyDec = decPart || "";
    }
    setFees({
      monthlyFee: monthlyFeeInt,
      monthlyFeeDecimal: monthlyFeeDec,
      perMessageReply: perMessageReplyInt,
      perMessageReplyDecimal: perMessageReplyDec,
      maxOperators: clientData.fees?.maximumOperators ?? "",
      unlimited: clientData.fees?.unlimitedOperators ?? true,
    });
    setWebDetails({
      websiteUrl: clientData.webDetails?.websiteUrl ?? "",
      smtpEnabled: clientData.webDetails?.smtpEnabled ?? false,
      smtpHost: clientData.webDetails?.smtpHost ?? "",
      smtpPort: clientData.webDetails?.smtpPort ?? "",
      smtpUsername: clientData.webDetails?.smtpUsername ?? "",
      smtpPassword: clientData.webDetails?.smtpPassword ?? "",
      smtpFrom: clientData.webDetails?.smtpFromAddress ?? "",
      smtpSSL: clientData.webDetails?.smtpRequiresSsl ?? false,
    });
  }, [clientData]);

  // Handlers for each tab
  const handleDetailsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Hide error as soon as user starts typing and field is not empty
    if (errors[name] && value.trim()) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };
  const handleGroupsChange = (e) => {
    setDetails((prev) => ({ ...prev, groups: e.target.value }));
  };
  const handleCreditChange = (idx, field, value) => {
    setCreditBundles((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
    );
    // Hide error as soon as user starts typing and both fields are not empty
    if (errors.creditBundles) {
      const bundle =
        idx === 0 ? { ...creditBundles[0], [field]: value } : creditBundles[0];
      if (bundle.credits && bundle.amount) {
        setErrors((prev) => ({ ...prev, creditBundles: undefined }));
      }
    }
  };
  const addCreditRow = () =>
    setCreditBundles((prev) => [...prev, { credits: "", amount: "" }]);
  const removeCreditRow = (idx) =>
    setCreditBundles((prev) => prev.filter((_, i) => i !== idx));
  const handleFeesChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFees((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Hide error as soon as user starts typing and field is not empty
    if (
      (name === "monthlyFee" || name === "perMessageReply") &&
      errors[name] &&
      value.trim()
    ) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };
  const handleWebChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWebDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Modern validation for all SMTP fields: clear error as soon as user types a valid value
    if (webDetails.smtpEnabled) {
      if (
        [
          "smtpHost",
          "smtpPort",
          "smtpUsername",
          "smtpPassword",
          "smtpFrom",
        ].includes(name)
      ) {
        if (errors[name] && value.trim()) {
          // For smtpHost, also check valid host
          if (name === "smtpHost") {
            const hostPattern =
              /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.?([A-Za-z0-9-]{1,63}\.?)*[A-Za-z]{2,6}$|^(\d{1,3}\.){3}\d{1,3}$/;
            if (hostPattern.test(value.trim())) {
              setErrors((prev) => ({ ...prev, smtpHost: undefined }));
            }
          } else {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
          }
        }
      }
    } else {
      // If SMTP is not enabled, clear all SMTP errors
      if (
        [
          "smtpHost",
          "smtpPort",
          "smtpUsername",
          "smtpPassword",
          "smtpFrom",
        ].includes(name) &&
        errors[name]
      ) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    }
  };

  // Add separate save handlers for each tab
  const handleSaveDetails = async () => {
    // Validate required fields for Details tab
    const newErrors = {};
    if (!details.name.trim()) newErrors.name = "Name is required";
    if (!details.email.trim()) newErrors.email = "Email is required";
    if (!details.telephone.trim())
      newErrors.telephone = "Contact Number is required";
    if (!details.address1.trim()) newErrors.address1 = "Address 1 is required";
    if (!details.city.trim()) newErrors.city = "Town/City is required";
    if (!details.postcode.trim())
      newErrors.postcode = "PostCode/ZipCode is required";
    if (!details.country.trim()) newErrors.country = "Country is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstErrorField}"]`);
      if (el && el.scrollIntoView)
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Prefix phone code
    const selectedCountry = countries.find((c) => c.code === details.country);
    const phoneCode = selectedCountry ? selectedCountry.phoneCode : "+44";
    const telephoneWithCode = details.telephone.startsWith(phoneCode)
      ? details.telephone
      : `${phoneCode}${details.telephone.trim()}`;

    setLoading(true);
    try {
      const payload = {
        name: details.name,
        email: details.email,
        contactNumber: telephoneWithCode,
        address1: details.address1,
        address2: details.address2,
        countyState: details.county,
        townCity: details.city,
        postCode: details.postcode,
        country: details.country,
        apiKey: details.secretKey,
        status: details.enabled ? 1 : 0,
        groups: details.groups, // Send group IDs to backend
      };
      const response = await api.put(`/clients/${id}/details`, payload);

      if (response.data.success) {
        showNotification(response.data.message, "success");
      } else {
        // Handle update failure
        showNotification(response.data.message, "error");
      }

      setLoading(false); // <-- Add this line
      setTab(1);
    } catch (err) {
      setLoading(false);
      showNotification("Failed to save details", "error");
    }
  };

  const handleSaveCreditBundles = async () => {
    const newErrors = {};
    if (!creditBundles[0].credits || !creditBundles[0].amount) {
      newErrors.creditBundles =
        "At least one bundle with Credits and Amount is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = creditBundles.map((b) => ({
        credits: b.credits ? Number(b.credits) : 0,
        amount: b.amount ? parseFloat(`${b.amount}.${b.decimal || "00"}`) : 0,
        currency: currency || "GBP",
      }));
      const response = await api.put(`/clients/${id}/credit-bundles`, payload);
      if (response.data.success) {
        showNotification(response.data.message, "success");
      } else {
        // Handle update failure
        showNotification(response.data.message, "error");
      }
      setLoading(false); // <-- Add this line
      setTab(2);
    } catch (err) {
      setLoading(false);
      showNotification("Failed to save credit bundles", "error");
    }
  };

  const handleSaveFees = async () => {
    const newErrors = {};
    if (!fees.monthlyFee) newErrors.monthlyFee = "Monthly Fee is required";
    if (!fees.perMessageReply)
      newErrors.perMessageReply = "Per Message Reply is required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        monthlyFee:
          fees.monthlyFee || fees.monthlyFeeDecimal
            ? parseFloat(
                `${fees.monthlyFee || 0}.${fees.monthlyFeeDecimal || "00"}`
              )
            : 0,
        perMessageReply:
          fees.perMessageReply || fees.perMessageReplyDecimal
            ? parseFloat(
                `${fees.perMessageReply || 0}.${
                  fees.perMessageReplyDecimal || "00"
                }`
              )
            : 0,
        maximumOperators: fees.maxOperators ? Number(fees.maxOperators) : 0,
        unlimitedOperators: !!fees.unlimited,
      };
      const response = await api.put(`/clients/${id}/fees`, payload);
      if (response.data.success) {
        showNotification(response.data.message, "success");
      } else {
        // Handle update failure
        showNotification(response.data.message, "error");
      }
      setLoading(false); // <-- Add this line
      setTab(3);
    } catch (err) {
      setLoading(false);
      showNotification("Failed to save fees", "error");
    }
  };

  const handleSaveWebDetails = async () => {
    const newErrors = {};
    if (!webDetails.websiteUrl.trim())
      newErrors.websiteUrl = "Website URL is required";
    if (webDetails.smtpEnabled) {
      if (!String(webDetails.smtpHost).trim())
        newErrors.smtpHost = "SMTP Host is required";
      if (!String(webDetails.smtpPort).trim())
        newErrors.smtpPort = "SMTP Port is required";
      if (!String(webDetails.smtpUsername).trim())
        newErrors.smtpUsername = "SMTP Username is required";
      if (!String(webDetails.smtpPassword).trim())
        newErrors.smtpPassword = "SMTP Password is required";
      if (!String(webDetails.smtpFrom).trim())
        newErrors.smtpFrom = "SMTP From Address is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      const payload = {
        websiteUrl: webDetails.websiteUrl,
        smtpEnabled: !!webDetails.smtpEnabled,
        smtpHost: webDetails.smtpHost,
        smtpPort: webDetails.smtpPort ? Number(webDetails.smtpPort) : null,
        smtpUsername: webDetails.smtpUsername,
        smtpPassword: webDetails.smtpPassword,
        smtpFromAddress: webDetails.smtpFrom,
        smtpRequiresSsl: !!webDetails.smtpSSL,
      };
      const response = await api.put(`/clients/${id}/web-details`, payload);
      if (response.data.success) {
        showNotification(response.data.message, "success");
      } else {
        // Handle update failure
        showNotification(response.data.message, "error");
      }
      setLoading(false); // <-- Add this line
      // Optionally show success or navigate
    } catch (err) {
      setLoading(false);
      showNotification("Failed to save web details", "error");
    }
  };

  if (clientLoading) return <Typography sx={{ mt: 4 }}>Loading...</Typography>;

  // Allow tab click navigation, but only allow moving forward if previous tabs are valid
  const handleTabChange = (_, v) => {
    if (v === tab) return;
    // If moving back, always allow
    if (v < tab) {
      setTab(v);
      return;
    }
    // If moving forward, validate all required fields in previous tabs
    let newErrors = {};
    if (tab < 1 && v >= 1) {
      // Validate Details tab
      if (!details.name.trim()) newErrors.name = "Name is required";
      if (!details.email.trim()) newErrors.email = "Email is required";
      if (!details.telephone.trim())
        newErrors.telephone = "Contact Number is required";
      if (!details.address1.trim())
        newErrors.address1 = "Address 1 is required";
      if (!details.city.trim()) newErrors.city = "Town/City is required";
      if (!details.postcode.trim())
        newErrors.postcode = "PostCode/ZipCode is required";
      if (!details.country.trim()) newErrors.country = "Country is required";
    }
    if (tab < 2 && v >= 2) {
      // Validate Credit Bundles tab
      if (!creditBundles[0].credits || !creditBundles[0].amount) {
        newErrors.creditBundles =
          "At least one bundle with Credits and Amount is required";
      }
    }
    if (tab < 3 && v >= 3) {
      // Validate Fees tab
      if (!fees.monthlyFee) newErrors.monthlyFee = "Monthly Fee is required";
      if (!fees.perMessageReply)
        newErrors.perMessageReply = "Per Message Reply is required";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error field
      const firstErrorField = Object.keys(newErrors)[0];
      const el = document.querySelector(`[name="${firstErrorField}"]`);
      if (el && el.scrollIntoView)
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setTab(v);
  };

  // Handler to open modal and fetch operators by group id
  const handleGroupInfoClick = async (groupId) => {
    const { success, data } = await fetchOperatorsByGroupId(groupId);
    if (success) {
      setOperatorRows(data || []);
      setOperatorModalOpen(true);
    } else {
      setOperatorRows([]);
      setOperatorModalOpen(true);
    }
  };

  return (
    <>
      <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "baseline",
              gap: 4,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ color: "var(--color-primary)", fontWeight: 700 }}
            >
              EDIT CLIENT
            </Typography>
            <div>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {details.name}
              </Typography>
            </div>
          </div>
          <Button
            variant="outlined"
            onClick={() => navigate("/superadmin/clients")}
            sx={{
              color: "#fff",
              borderColor: "#635f5fff",
              backgroundColor: "#635f5fff",
              "&:hover": { backgroundColor: "#333", borderColor: "#333" },
            }}
          >
            Back to Clients
          </Button>
        </div>
        <Paper sx={{ width: "100%" }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                color: "#666",
                "&.Mui-selected": {
                  color: "var(--color-primary)",
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "var(--color-primary)",
              },
            }}
          >
            <Tab label="Details" />
            <Tab label="Credit Bundles" />
            <Tab label="Fees" />
            <Tab label="Web Details" />
            <Tab label="Payment" />
             <Tab label="Custom" />
          </Tabs>
          <TabPanel value={tab} index={5}>
  <Box
    sx={{
      p: 2,
      backgroundColor: "#f8f9fa",
      minHeight: 320,
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
      }}
    >
      {/* TEXTAREA */}
      <FormControl error={!!errors.message} sx={{ width: "100%" }}>
        <InputLabel
          shrink={true}
          sx={{ backgroundColor: "#fafafa", fontSize: "16px", px: 0.5 }}
        >
          Acknowledgement Message
        </InputLabel>

        <Box
          component="textarea"
          name="message"
          required
          rows={10}
          maxLength={160}
          value={safeMessage}
          placeholder="Enter your acknowledgement message here..."
          onChange={(e) =>
            setSafeMessage(e.target.value || "")
          }
          sx={{
            width: "70%",              // ✅ full width
            boxSizing: "border-box",
            padding: "16px",
            fontSize: "16px",
            fontFamily: "Roboto, sans-serif",
            borderRadius: "10px",
            border: errors.message
              ? "1px solid #d32f2f"
              : "1px solid #c4c4c4",
            outline: "none",
            resize: "vertical",
            backgroundColor: "#fafafa",
            caretColor: "#1976d2",
            "&:focus": {
              border: "2px solid #1976d2",
            },
            "&::placeholder": {
              color: "#999",
              opacity: 1,
            },
          }}
        />

        <FormHelperText>
  {errors.message
    ? errors.message
    : safeMessage.trim().length < 10
    ? "Minimum 10 characters required"
    : safeMessage.trim().length > 160
    ? "Maximum 160 characters allowed"
    : `${safeMessage.trim().length}/160 characters`}
</FormHelperText>

      </FormControl>

      {/* SAVE BUTTON */}
      <Button
        variant="contained"
        type="button"
        onClick={handleSubmitTab}
        disabled={safeMessage.trim().length < 10 || safeMessage.trim().length > 160}
        sx={{ height: 40, width: 180 }}
      >
        Save
      </Button>
    </Box>
  </Box>
</TabPanel>


          <TabPanel value={tab} index={0}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                minHeight: 460,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
              >
                Client Details
              </Typography>

              <Box sx={{ display: "flex", gap: 4 }}>
                {/* Left Column*/}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    flex: 1,
                    maxWidth: 600,
                  }}
                >
                  {/* Name, Enabled Row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      label="Name"
                      name="name"
                      required
                      autoFocus
                      value={details.name}
                      // onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "name",
                          sanitizeInput(e.target.value),
                          setDetails,
                          setErrors
                        )
                      }
                      size="medium"
                      placeholder="Name"
                      inputProps={{ maxLength: 30 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.name}
                      helperText={errors.name}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        ml: 1,
                        gap: 0.5,
                        height: 36,
                      }}
                    >
                      <Switch
                        checked={details.enabled}
                        onChange={(e) =>
                          setDetails((prev) => ({
                            ...prev,
                            enabled: e.target.checked,
                          }))
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#43a047", // light green
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#43a047",
                            },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.92rem",
                          color: "#333",
                          fontWeight: 500,
                          userSelect: "none",
                          lineHeight: 1,
                        }}
                      >
                        Enabled
                      </Typography>
                    </Box>
                    <style>{}</style>
                  </Box>
                  {/* Telephone and Email Row */}
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                      fullWidth
                      label="Telephone"
                      name="telephone"
                      required
                      value={details.telephone}
                      onChange={(e) =>
                        handleFieldChange(
                          "telephone",
                          e.target.value.replace(/[^0-9]/g, ""),
                          setDetails,
                          setErrors
                        )
                      }
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        background: "#fff",
                      }}
                      inputProps={{ maxLength: 15 }}
                      InputProps={{
                        startAdornment: (
                          <Box
                            sx={{
                              minWidth: 64,
                              mr: 1,
                              pr: 1,
                              borderRight: "2px solid #E9E9E9",
                            }}
                          >
                            {phoneCode}
                          </Box>
                        ),
                      }}
                    />

                    <TextField
                      label="Email"
                      name="email"
                      required
                      value={details.email}
                      // onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "email",
                          e.target.value,
                          setDetails,
                          setErrors
                        )
                      }
                      size="medium"
                      placeholder="Email"
                      inputProps={{ maxLength: 255 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Box>

                  {/* Address Row */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="Address"
                      name="address1"
                      required
                      value={details.address1}
                      //onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "address1",
                          e.target.value,
                          setDetails,
                          setErrors
                        )
                      }
                      size="medium"
                      placeholder="Address 1"
                      inputProps={{ maxLength: 50 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.address1}
                      helperText={errors.address1}
                    />
                    <TextField
                      label="Address 2"
                      name="address2"
                      value={details.address2}
                      onChange={handleDetailsChange}
                      size="medium"
                      placeholder="Address 2"
                      inputProps={{ maxLength: 30 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                    />
                  </Box>
                  {/* County/State and Town/City Row */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="County/State"
                      name="county"
                      value={details.county}
                      //onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "county",
                          e.target.value,
                          setDetails,
                          setErrors,
                          "County/State"
                        )
                      }
                      size="medium"
                      placeholder="County/State"
                      inputProps={{ maxLength: 30 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.county}
                      helperText={errors.county}
                    />
                    <TextField
                      label="Town/City"
                      name="city"
                      required
                      value={details.city}
                      //   onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "city",
                          e.target.value,
                          setDetails,
                          setErrors,
                          "Town/City"
                        )
                      }
                      size="medium"
                      placeholder="Town/City"
                      inputProps={{ maxLength: 30 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.city}
                      helperText={errors.city}
                    />
                  </Box>
                  {/* PostCode/ZipCode and Country Row */}
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="PostCode/ZipCode"
                      name="postcode"
                      required
                      value={details.postcode}
                      // onChange={handleDetailsChange}
                      onChange={(e) =>
                        handleFieldChange(
                          "postcode",
                          e.target.value,
                          setDetails,
                          setErrors
                        )
                      }
                      size="medium"
                      placeholder="PostCode/ZipCode"
                      inputProps={{ maxLength: 10 }}
                      sx={{
                        width: 270,
                        minWidth: 180,
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.postcode}
                      helperText={errors.postcode}
                    />
                    <Box sx={{ width: 270, minWidth: 180 }}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        size="medium"
                        sx={{
                          borderRadius: 2,
                          background: "#fff",
                          width: "100%",
                          height: 40,
                          border: errors.country
                            ? "1.5px solid #d32f2f"
                            : undefined,
                        }}
                        error={!!errors.country}
                      >
                        <InputLabel
                          htmlFor="country-select"
                          sx={{
                            transform: "translate(14px, 18px) scale(0.9)",
                            "&.MuiInputLabel-shrink": {
                              transform: "translate(14px, 0px) scale(0.75)",
                            },
                          }}
                        >
                          Country
                        </InputLabel>
                        <Select
                          name="country"
                          value={details.country}
                          onChange={(e) => {
                            handleDetailsChange(e);
                            if (errors.country && e.target.value) {
                              setErrors((prev) => ({
                                ...prev,
                                country: undefined,
                              }));
                            }
                          }}
                          onBlur={(e) => {
                            if (!e.target.value)
                              setErrors((prev) => ({
                                ...prev,
                                country: "Country is required",
                              }));
                          }}
                          input={<OutlinedInput label="Country" />}
                          sx={{
                            "& .MuiSelect-select": { py: 2, fontSize: "1rem" },
                            "& .MuiInputBase-input::placeholder": {
                              fontSize: "0.75rem",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 220,
                                overflowY: "auto",
                              },
                            },
                          }}
                        >
                          {countries.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <Box sx={{ mt: 0.5, ml: 1 }}>
                            <span
                              style={{ color: "#d32f2f", fontSize: "0.75rem" }}
                            >
                              {errors.country}
                            </span>
                          </Box>
                        )}
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
                {/* Right Column */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <TextField
                      label="Secret Key"
                      name="secretKey"
                      value={details.secretKey}
                      placeholder="Secret Key"
                      inputProps={{
                        readOnly: true,
                        sx: { height: 56 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                navigator.clipboard.writeText(details.secretKey)
                              }
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                        style: { fontSize: "1rem" },
                      }}
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        background: "#fff",
                        minWidth: 180,
                        width: "100%",
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        background: "#fff",
                        minWidth: 180,
                        maxWidth: 300,
                      }}
                    >
                      <InputLabel
                        htmlFor="groups-select"
                        sx={{
                          transform: "translate(14px, 18px) scale(0.9)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, 0px) scale(0.75)",
                          },
                        }}
                      >
                        Groups
                      </InputLabel>
                      <Select
                        multiple
                        value={details.groups}
                        onChange={handleGroupsChange}
                        input={
                          <OutlinedInput label="Groups" placeholder="Groups" />
                        }
                        renderValue={(selected) => (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              overflowX: "auto", // Enable horizontal scroll
                              maxWidth: 250, // Set a max width as needed
                              whiteSpace: "nowrap",
                              // Hide scrollbar for Webkit browsers (Chrome, Safari, Edge)
                              "&::-webkit-scrollbar": { display: "none" },
                              // Hide scrollbar for Firefox
                              scrollbarWidth: "none",
                              // Hide scrollbar for IE/Edge
                              msOverflowStyle: "none",
                            }}
                          >
                            {selected.map((groupId) => {
                              const group = groups.find(
                                (g) => g.id === groupId
                              );
                              return (
                                <Chip
                                  fontSize={"0.5rem"}
                                  key={groupId}
                                  label={
                                    group?.name || group?.groupName || groupId
                                  }
                                />
                              );
                            })}
                          </Box>
                        )}
                        sx={{
                          "& .MuiSelect-select": { py: 2, fontSize: "1rem" },
                          "& .MuiInputBase-input::placeholder": {
                            fontSize: "1rem",
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        {groups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                position: "relative",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={details.groups.indexOf(group.id) > -1}
                                readOnly
                                style={{ marginRight: 8 }}
                              />
                              <Typography>
                                {group.name || group.groupName}
                              </Typography>
                              <InfoOutlineIcon
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevents checkbox/select toggling
                                  handleGroupInfoClick(group.id);
                                }}
                                sx={{
                                  position: "absolute",
                                  right: 0,
                                  cursor: "pointer",
                                }}
                              />
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
              {/* Move Next button inside the Paper card for better alignment */}
              <Box sx={{ position: "absolute", bottom: 16, right: 16 }}>
                <Button
                  variant="contained"
                  disabled={Object.values(errors).some((msg) => msg)}
                  onClick={handleSaveDetails}
                  sx={{
                    mt: 2,
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-secondary)" },
                  }}
                >
                  Save & Next
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                minHeight: 460,
                position: "relative",
              }}
            >
              <Box sx={{ display: "flex", gap: 4, maxWidth: 600, mb: 3 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
                >
                  Credit Bundles
                </Typography>
                <FormControl
                  variant="outlined"
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    background: "#fff",
                    width: "200px",
                    height: 40,
                  }}
                >
                  <InputLabel
                    htmlFor="currency-select"
                    sx={{
                      transform: "translate(14px, 18px) scale(0.9)",
                      "&.MuiInputLabel-shrink": {
                        transform: "translate(14px, 0px) scale(0.75)",
                      },
                    }}
                  >
                    Currency
                  </InputLabel>
                  <Select
                    name="currency"
                 
                    value={currency}
                    onChange={(e) => {
                      setCurrency(e.target.value);
                    }}
                    input={<OutlinedInput label="Currency" />}
                    sx={{
                      "& .MuiSelect-select": { py: 2, fontSize: "1rem" },
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <MenuItem key="GBP" value="GBP">
                      GBP (£)
                    </MenuItem>
                    <MenuItem key="AUD" value="AUD">
                      AUD ($)
                    </MenuItem>
                    <MenuItem key="EUR" value="EUR">
                      EUR (€)
                    </MenuItem>
                    <MenuItem key="USD" value="USD">
                      USD ($)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 5 }}
              >
                {creditBundles.map((row, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <TextField
                      label="Credits"
                       autoFocus={true}
                      value={row.credits}
                      onChange={(e) =>
                        handleCreditChange(
                          idx,
                          "credits",
                          sanitizeNumberOnly(e.target.value)
                        )
                      }
                      onBlur={(e) => {
                        if (
                          idx === 0 &&
                          (!e.target.value || !creditBundles[0].amount)
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            creditBundles:
                              "At least one bundle with Credits and Amount is required",
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="Credits"
                      inputProps={{
                        sx: { height: 50 },
                        style: { fontSize: "0.95rem" },
                        maxLength: 6,
                      }}
               
                      sx={{
                        width: 130,
                        minWidth: 90,
                        borderRadius: 2,
                        background: "#fff",
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                    <TextField
                      label="Amount"
                      value={row.amount}
                      onChange={(e) =>
                        handleCreditChange(
                          idx,
                          "amount",
                          sanitizeNumberOnly(e.target.value)
                        )
                      }
                      onBlur={(e) => {
                        if (
                          idx === 0 &&
                          (!e.target.value || !creditBundles[0].credits)
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            creditBundles:
                              "At least one bundle with Credits and Amount is required",
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="Amount"
                      inputProps={{
                        sx: { height: 50 },
                        style: { fontSize: "0.95rem" },
                        maxLength: 8,
                      }}
                      sx={{
                        width: 150,
                        minWidth: 100,
                        borderRadius: 2,
                        background: "#fff",
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.7rem",
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "1.7rem",
                        color: "#a12a22",
                        mx: 0,
                        minWidth: 0,
                        userSelect: "none",
                        fontWeight: 700,
                        lineHeight: 1,
                        p: 0,
                      }}
                    >
                      .
                    </Typography>
                    <TextField
                      value={row.decimal || ""}
                      onChange={(e) =>
                        handleCreditChange(
                          idx,
                          "decimal",
                          e.target.value.replace(/[^0-9]/g, "").slice(0, 2)
                        )
                      }
                      size="medium"
                      placeholder="00"
                      inputProps={{
                        sx: { height: 50, p: 0 },
                        style: {
                          fontSize: "0.95rem",
                          textAlign: "center",
                          paddingLeft: 0,
                        },
                        maxLength: 2,
                      }}
                      sx={{
                        width: 80,
                        minWidth: 60,
                        borderRadius: 2,
                        background: "#fff",
                        mx: 0,
                        p: 0,
                        "& .MuiInputBase-input::placeholder": {
                          fontSize: "0.7rem",
                          textAlign: "center",
                          paddingLeft: 0,
                        },
                      }}
                    />
                    <IconButton
                      color="primary"
                      onClick={addCreditRow}
                      size="small"
                      sx={{
                        borderRadius: 2,
                        background: "#e3eafc",
                        p: 0.5,
                        ml: 0.5,
                        height: 32,
                        width: 32,
                        "&:hover": { background: "#dbe2fa" },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                    {creditBundles.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => removeCreditRow(idx)}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          background: "#fdeaea",
                          p: 0.5,
                          height: 32,
                          width: 32,
                          "&:hover": { background: "#f8d7da" },
                        }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {errors.creditBundles && (
                  <Typography
                    sx={{ color: "#d32f2f", fontSize: "0.8rem", mt: 1 }}
                  >
                    {errors.creditBundles}
                  </Typography>
                )}
              </Box>
              <Box sx={{ position: "absolute", bottom: 16, left: 16 }}>
                <Button
                  variant="contained"
                  onClick={() => setTab(0)}
                  sx={{
                    mt: 2,
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-secondary)" },
                  }}
                >
                  Previous
                </Button>
              </Box>
              <Box sx={{ position: "absolute", bottom: 16, right: 16 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveCreditBundles}
                  sx={{
                    mt: 2,
                    backgroundColor: "var(--color-primary)",
                    "&:hover": { backgroundColor: "var(--color-secondary)" },
                  }}
                >
                  Save & Next
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                minHeight: 460,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
              >
                Fees Details
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    label="Monthly Fee"
                    name="monthlyFee"
                    value={fees.monthlyFee}
                    autoFocus = {true}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      handleFeesChange({
                        target: {
                          name: "monthlyFee",
                          value,
                          type: "text",
                        },
                      });
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim())
                        setErrors((prev) => ({
                          ...prev,
                          monthlyFee: "Monthly Fee is required",
                        }));
                    }}
                    size="medium"
                    placeholder="Monthly Fee"
                    inputProps={{
                      sx: { height: 48 },
                      style: { fontSize: "1rem" },
                      maxLength: 8,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      width: 150,
                      minWidth: 90,
                      borderRadius: 2,
                      background: "#fff",
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                      },
                    }}
                    error={!!errors.monthlyFee}
                    helperText={errors.monthlyFee}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.7rem",
                      color: "#a12a22",
                      mx: 0,
                      minWidth: 0,
                      userSelect: "none",
                      fontWeight: 700,
                      lineHeight: 1,
                      p: 0,
                    }}
                  >
                    .
                  </Typography>
                  <TextField
                    value={fees.monthlyFeeDecimal || ""}
                    onChange={(e) =>
                      setFees((prev) => ({
                        ...prev,
                        monthlyFeeDecimal: e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 2),
                      }))
                    }
                    size="medium"
                    placeholder="00"
                    inputProps={{
                      sx: { height: 48, p: 0 },
                      style: {
                        fontSize: "1rem",
                        textAlign: "center",
                        paddingLeft: 0,
                      },
                      maxLength: 2,
                    }}
                    sx={{
                      width: 60,
                      minWidth: 40,
                      borderRadius: 2,
                      background: "#fff",
                      mx: 0,
                      p: 0,
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                        textAlign: "center",
                        paddingLeft: 0,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    label="Per Message Reply"
                    name="perMessageReply"
                    value={fees.perMessageReply}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      handleFeesChange({
                        target: {
                          name: "perMessageReply",
                          value,
                          type: "text",
                        },
                      });
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim())
                        setErrors((prev) => ({
                          ...prev,
                          perMessageReply: "Per Message Reply is required",
                        }));
                    }}
                    size="medium"
                    placeholder="Per Message Reply"
                    inputProps={{
                      sx: { height: 48 },
                      style: { fontSize: "1rem" },
                      maxLength: 8,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      width: 150,
                      minWidth: 90,
                      borderRadius: 2,
                      background: "#fff",
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                      },
                    }}
                    error={!!errors.perMessageReply}
                    helperText={errors.perMessageReply}
                  />
                  <Typography
                    sx={{
                      fontSize: "1.7rem",
                      color: "#a12a22",
                      mx: 0,
                      minWidth: 0,
                      userSelect: "none",
                      fontWeight: 700,
                      lineHeight: 1,
                      p: 0,
                    }}
                  >
                    .
                  </Typography>
                  <TextField
                    value={fees.perMessageReplyDecimal || ""}
                    onChange={(e) =>
                      setFees((prev) => ({
                        ...prev,
                        perMessageReplyDecimal: e.target.value
                          .replace(/[^0-9]/g, "")
                          .slice(0, 2),
                      }))
                    }
                    size="medium"
                    placeholder="00"
                    inputProps={{
                      sx: { height: 48, p: 0 },
                      style: {
                        fontSize: "1rem",
                        textAlign: "center",
                        paddingLeft: 0,
                      },
                      maxLength: 2,
                    }}
                    InputLabelProps={{
                      sx: {
                        transform: "translate(14px, 18px) scale(0.9)",
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, 0px) scale(0.75)",
                        },
                        "& .MuiInputLabel-asterisk": {
                          opacity: 0,
                        },
                        "&.MuiInputLabel-shrink .MuiInputLabel-asterisk": {
                          opacity: 1,
                          color: "#d32f2f",
                        },
                      },
                    }}
                    sx={{
                      width: 60,
                      minWidth: 40,
                      borderRadius: 2,
                      background: "#fff",
                      mx: 0,
                      p: 0,
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                        textAlign: "center",
                        paddingLeft: 0,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    label="Maximum Operators"
                    name="maxOperators"
                    value={fees.maxOperators}
                    disabled={fees.unlimited}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      handleFeesChange({
                        target: {
                          name: "maxOperators",
                          value,
                          type: "text",
                        },
                      });
                    }}
                    size="medium"
                    placeholder="Maximum Operators"
                    inputProps={{
                      sx: { height: 48 },
                      style: { fontSize: "1rem" },
                      maxLength: 2,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    sx={{
                      width: 210,
                      minWidth: 120,
                      borderRadius: 2,
                      background: "#fff",
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                  <FormControlLabel
                    style={{ height: 48, minWidth: 100 }}
                    control={
                      <Switch
                        checked={fees.unlimited}
                        onChange={(e) =>
                          setFees((prev) => ({
                            ...prev,
                            unlimited: e.target.checked,
                          }))
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#43a047", // light green
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "#43a047",
                            },
                        }}
                      />
                    }
                    label="Unlimited"
                    sx={{ ml: 2 }}
                  />
                </Box>

                {(errors.monthlyFee || errors.perMessageReply) && (
                  <Typography
                    sx={{ color: "#d32f2f", fontSize: "0.8rem", mt: 1 }}
                  >
                    {errors.monthlyFee ? errors.monthlyFee : ""}{" "}
                    {errors.perMessageReply ? errors.perMessageReply : ""}
                  </Typography>
                )}
                <Box sx={{ position: "absolute", bottom: 16, left: 16 }}>
                  <Button
                    variant="contained"
                    onClick={() => setTab(1)}
                    sx={{
                      mt: 2,
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    Previous
                  </Button>
                </Box>
                <Box sx={{ position: "absolute", bottom: 16, right: 16 }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveFees}
                    sx={{
                      mt: 2,
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    Save & Next
                  </Button>
                </Box>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#f8f9fa",
                minHeight: 460,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
              >
                Web Details
              </Typography>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                  >
                    Website URL
                  </Typography>
                  <TextField
                    label="Website URL"
                    name="websiteUrl"
                    autoFocus={true}
                    value={webDetails.websiteUrl}
                    onChange={handleWebChange}
                    onBlur={(e) => {
                      if (!e.target.value.trim())
                        setErrors((prev) => ({
                          ...prev,
                          websiteUrl: "Website URL is required",
                        }));
                    }}
                    size="medium"
                    placeholder="Website URL"
                    inputProps={{
                      sx: { height: 48 },
                      style: { fontSize: "1rem" },
                    }}
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                    error={!!errors.websiteUrl}
                    helperText={errors.websiteUrl}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#333" }}
                  >
                    SMTP Details
                  </Typography>
                  {/* Row 1: SMTP Enabled toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={webDetails.smtpEnabled}
                          onChange={(e) =>
                            setWebDetails((prev) => ({
                              ...prev,
                              smtpEnabled: e.target.checked,
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#43a047", // light green
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#43a047",
                              },
                          }}
                        />
                      }
                      label="SMTP Enabled"
                      sx={{ mr: 2 }}
                    />
                  </Box>
                  {/* Row 2: Host and Port */}
                  <Box
                    sx={{ display: "flex", gap: 2, flexWrap: "nowrap", mb: 1 }}
                  >
                    <TextField
                      label="SMTP Host"
                      name="smtpHost"
                      value={webDetails.smtpHost}
                      onChange={handleWebChange}
                      onBlur={(e) => {
                        if (webDetails.smtpEnabled && !e.target.value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpHost: "SMTP Host is required",
                          }));
                        } else if (
                          webDetails.smtpEnabled &&
                          e.target.value.trim()
                        ) {
                          // Validate hostname or IP
                          const hostPattern =
                            /^(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.?([A-Za-z0-9-]{1,63}\.?)*[A-Za-z]{2,6}$|^(\d{1,3}\.){3}\d{1,3}$/;
                          if (!hostPattern.test(e.target.value.trim())) {
                            setErrors((prev) => ({
                              ...prev,
                              smtpHost: "Enter a valid hostname or IP address",
                            }));
                          } else if (errors.smtpHost) {
                            setErrors((prev) => ({
                              ...prev,
                              smtpHost: undefined,
                            }));
                          }
                        } else if (!webDetails.smtpEnabled && errors.smtpHost) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpHost: undefined,
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="SMTP Host"
                      inputProps={{
                        sx: { height: 48 },
                        style: { fontSize: "1rem" },
                      }}
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        width: "100%",
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.smtpHost}
                      helperText={errors.smtpHost}
                    />
                    <TextField
                      label="SMTP Port"
                      name="smtpPort"
                      value={webDetails.smtpPort}
                      onChange={handleWebChange}
                      onBlur={(e) => {
                        if (webDetails.smtpEnabled && !e.target.value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpPort: "SMTP Port is required",
                          }));
                        } else if (!webDetails.smtpEnabled && errors.smtpPort) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpPort: undefined,
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="SMTP Port"
                      inputProps={{
                        sx: { height: 48 },
                        style: { fontSize: "1rem" },
                      }}
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        width: "100%",
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.smtpPort}
                      helperText={errors.smtpPort}
                    />
                  </Box>
                  {/* Row 3: Username and Password */}
                  <Box
                    sx={{ display: "flex", gap: 2, flexWrap: "nowrap", mb: 1 }}
                  >
                    <TextField
                      label="SMTP Username"
                      name="smtpUsername"
                      value={webDetails.smtpUsername}
                      onChange={handleWebChange}
                      onBlur={(e) => {
                        if (webDetails.smtpEnabled && !e.target.value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpUsername: "SMTP Username is required",
                          }));
                        } else if (
                          !webDetails.smtpEnabled &&
                          errors.smtpUsername
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpUsername: undefined,
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="SMTP Username"
                      inputProps={{
                        sx: { height: 48 },
                        style: { fontSize: "1rem" },
                      }}
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        width: "100%",
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.smtpUsername}
                      helperText={errors.smtpUsername}
                    />
                    <TextField
                      label="SMTP Password (App/Google Password)"
                      name="smtpPassword"
                      type="password"
                      value={webDetails.smtpPassword}
                      onChange={handleWebChange}
                      onBlur={(e) => {
                        if (webDetails.smtpEnabled && !e.target.value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpPassword: "SMTP Password is required",
                          }));
                        } else if (
                          !webDetails.smtpEnabled &&
                          errors.smtpPassword
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpPassword: undefined,
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="App/Google Password"
                      inputProps={{
                        sx: { height: 48 },
                        style: { fontSize: "1rem" },
                      }}
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        width: "100%",
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.smtpPassword}
                      helperText={errors.smtpPassword}
                    />
                  </Box>
                  {/* Row 4: From Address and Requires SSL toggle */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      flexWrap: "nowrap",
                      mb: 1,
                    }}
                  >
                    <TextField
                      label="From Address"
                      name="smtpFrom"
                      value={webDetails.smtpFrom}
                      onChange={handleWebChange}
                      onBlur={(e) => {
                        if (webDetails.smtpEnabled && !e.target.value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpFrom: "From Address is required",
                          }));
                        } else if (!webDetails.smtpEnabled && errors.smtpFrom) {
                          setErrors((prev) => ({
                            ...prev,
                            smtpFrom: undefined,
                          }));
                        }
                      }}
                      size="medium"
                      placeholder="From Address"
                      inputProps={{
                        sx: { height: 48 },
                        style: { fontSize: "1rem" },
                      }}
                      sx={{
                        minWidth: 180,
                        maxWidth: 270,
                        width: "100%",
                        borderRadius: 2,
                        background: "#fff",
                      }}
                      error={!!errors.smtpFrom}
                      helperText={errors.smtpFrom}
                    />
                    <FormControlLabel
                      style={{ height: 48, minWidth: 100 }}
                      control={
                        <Switch
                          checked={webDetails.smtpSSL}
                          onChange={(e) =>
                            setWebDetails((prev) => ({
                              ...prev,
                              smtpSSL: e.target.checked,
                            }))
                          }
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#43a047", // light green
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: "#43a047",
                              },
                          }}
                        />
                      }
                      label="Requires SSL"
                      sx={{ ml: 2 }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  mt: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setTab(2)}
                >
                  Previous
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    await handleSaveWebDetails();
                    setTab(4); // Go to Payment tab after save
                  }}
                >
                  Save & Next
                </Button>
              </Box>
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={4}>
            {/* Payment Tab Content */}
            <PaymentTab setTab={setTab} />
          </TabPanel>
        </Paper>
      </div>
      <OperatorListModal
        open={operatorModalOpen}
        onClose={() => setOperatorModalOpen(false)}
        operators={operatorRows}
      />
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </>
  );
}
